import React from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import TableItems from '../Table/TableItems'
import InvoiceFileActions from '../Table/InvoiceFileActions'
import { Transaction } from '../../interfaces/transaction'
import { CsvColumn } from '../../utils/toCsv'
import { formatPaymentMethod } from '../../helpers/formatPaymentMethod'
import { formatPaymentStatus } from '../../helpers/formatPaymentStatus'
import {
  sortDateNullsLast,
  sortNumericNullsLast,
  sortTextNullsLast,
} from '../../utils/tableSort'

const DEFAULT_SORT = [{ id: 'id', desc: true }]

interface Props {
  isLoading: boolean
  transactions: Transaction[]
}

const formatDateTime = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YYYY, HH:mm') : '-'

const formatBillingPeriod = (transaction: Transaction) => {
  const { billing_period_start: start, billing_period_end: end } = transaction
  if (!start && !end) return '-'
  const from = start ? moment(start).format('MM/YYYY') : '-'
  const to = end ? moment(end).format('MM/YYYY') : '-'
  return `${from}-${to}`
}

const formatAmount = (transaction: Transaction) =>
  transaction.amount ? `€${transaction.amount}` : '-'

// Shared by the search box and the CSV export so both describe the same rows.
export const transactionCsvColumns = (
  t: (key: string) => string,
): CsvColumn<Transaction>[] => [
  { header: 'ID', value: (row) => row.id },
  { header: t('invoiceNo'), value: (row) => row.invoice_no ?? '' },
  { header: t('chargeDate'), value: (row) => formatDateTime(row.charge_date) },
  {
    header: t('status'),
    value: (row) => formatPaymentStatus(t, row.status).label,
  },
  { header: t('billingPeriod'), value: (row) => formatBillingPeriod(row) },
  { header: t('amount'), value: (row) => formatAmount(row) },
  {
    header: t('method'),
    value: (row) => formatPaymentMethod(t, row.method),
  },
]

const TransactionsList = ({ isLoading, transactions = [] }: Props) => {
  const { t } = useTranslation()

  const columns = [
    {
      Header: 'ID',
      accessor: 'id',
      sortType: sortNumericNullsLast,
    },
    {
      Header: t('invoiceNo'),
      accessor: 'invoice_no',
      sortType: sortTextNullsLast,
      Cell: ({ value }: { value: Transaction['invoice_no'] }) =>
        value ? <span className='text-black/80'>{value}</span> : '-',
    },
    {
      Header: t('chargeDate'),
      accessor: 'charge_date',
      sortType: sortDateNullsLast,
      Cell: ({ value }: { value: Transaction['charge_date'] }) => (
        <span>{formatDateTime(value)}</span>
      ),
    },
    {
      Header: t('status'),
      accessor: 'status',
      sortType: sortTextNullsLast,
      Cell: ({ value }: { value: Transaction['status'] }) => {
        const { label, className } = formatPaymentStatus(t, value)
        return <span className={className}>{label}</span>
      },
    },
    {
      Header: t('billingPeriod'),
      // Renders from the row, but sorts on the start of the period.
      accessor: 'billing_period_start',
      sortType: sortDateNullsLast,
      Cell: ({ row: { original } }: { row: { original: Transaction } }) => (
        <span>{formatBillingPeriod(original)}</span>
      ),
    },
    {
      Header: t('amount'),
      // `amount` is a string, so it needs a numeric sort to keep 100 above 20.
      accessor: 'amount',
      sortType: sortNumericNullsLast,
      Cell: ({ row: { original } }: { row: { original: Transaction } }) => (
        <span>{formatAmount(original)}</span>
      ),
    },
    {
      Header: t('method'),
      accessor: 'method',
      sortType: sortTextNullsLast,
      Cell: ({ value }: { value: Transaction['method'] }) => (
        <span>{formatPaymentMethod(t, value)}</span>
      ),
    },
    {
      id: 'actions',
      Header: ' ',
      disableSortBy: true,
      Cell: ({ row: { original } }: { row: { original: Transaction } }) => (
        <InvoiceFileActions fileUrl={original.invoice_file} />
      ),
    },
  ]

  return (
    <div className='w-full min-h-[710px] px-5 py-5 md:px-10 md:py-6 lg:px-20 lg:py-11 bg-white rounded-20px shadow-form mt-7'>
      <TableItems
        columns={columns}
        data={transactions}
        isDataLoading={isLoading}
        noDataFoundMessage='noTransactionsFound'
        showSearch={false}
        isSortable
        // Newest first: the backend hands these over oldest-first for
        // organisations and by charge date for payments, and neither is what a
        // super admin opening the list wants to see at the top.
        defaultSortBy={DEFAULT_SORT}
        showLastColumn={false}
        pageSize={6}
        tableHeightClassName='h-[660px]'
      />
    </div>
  )
}

export default TransactionsList
